/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: trusted editor HTML */
"use client";
import { Dialog } from "@base-ui/react";
import { Avatar } from "@base-ui/react/avatar";
import { Menu } from "@base-ui/react/menu";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flag, Pencil, Trash } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import verified from "#/assets/badge/verified-badge.svg";
import {
  deleteCommentAction,
  editCommentAction,
  reportCommentAction,
} from "@/actions/content.action";
import { uploadFile } from "@/actions/file.action";
import TopicReplyEditor from "@/app/(main)/forum/_components/topic-reply-editor";
import ChevronDown from "@/components/icons/chevron-down";
import ElipsisIcon from "@/components/icons/elipsis";
import MessageIcon from "@/components/icons/message";
import { Button } from "@/components/ui/button";
import type { Comment } from "@/types/content";
import CommentLikeButton from "./comment-like-button";
import CommentReplies from "./comment-replies";
import CommentReplyForm from "./comment-reply-form";

const MAX_TEXT_LENGTH = 200;

type CurrentUser = { name: string; surname: string; imageUrl?: string };

interface Props {
  comment: Comment;
  contentId: string;
  isLoggedIn: boolean;
  currentUser?: CurrentUser;
  isReply?: boolean;
  parentCommentId?: string;
}

const CommentCard = ({
  comment,
  contentId,
  isLoggedIn,
  currentUser,
  isReply = false,
  parentCommentId,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const queryClient = useQueryClient();

  const isLong = comment.text.length > MAX_TEXT_LENGTH;
  const displayText =
    isLong && !expanded
      ? `${comment.text.slice(0, MAX_TEXT_LENGTH)}...`
      : comment.text;

  const initials = comment.userName
    .split(" ")
    .map((n) => n[0])
    .join("");

  const { mutate: editMutate, isPending: isEditPending } = useMutation({
    mutationFn: async ({ html, images }: { html: string; images: File[] }) => {
      let newImageUrls: string[] = [];
      if (images.length > 0) {
        const results = await Promise.all(
          images.map((f) => uploadFile(f, "content/comments")),
        );
        newImageUrls = results.flatMap((r) =>
          r.isSuccess ? [r.result.filePath] : [],
        );
      }
      return editCommentAction(comment.id, html, [
        ...comment.imageUrls,
        ...newImageUrls,
      ]);
    },
    onSuccess: (res) => {
      if (res.isSuccess) {
        queryClient.invalidateQueries({ queryKey: ["comments", contentId] });
        if (isReply) {
          queryClient.invalidateQueries({ queryKey: ["replies", comment.id] });
        }
        setIsEditing(false);
      }
    },
  });

  const handleReport = async () => {
    await reportCommentAction(comment.id);
    setReportOpen(false);
  };

  const { mutate: deleteMutate, isPending: isDeletePending } = useMutation({
    mutationFn: () => deleteCommentAction(comment.id),
    onSuccess: (res) => {
      if (res.isSuccess) {
        setDeleteOpen(false);
        if (isReply && parentCommentId) {
          queryClient.invalidateQueries({ queryKey: ["replies", parentCommentId] });
        } else {
          queryClient.invalidateQueries({ queryKey: ["comments", contentId] });
        }
      }
    },
  });

  return (
    <div className="py-4">
      <div className="flex flex-row gap-3">
        <Avatar.Root className="inline-flex size-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 align-middle text-sm font-medium text-gray-900 select-none shrink-0">
          {comment.userAvatar && (
            <Avatar.Image
              className="size-full object-cover"
              src={comment.userAvatar}
            />
          )}
          <Avatar.Fallback className="flex size-full items-center justify-center text-sm">
            {initials}
          </Avatar.Fallback>
        </Avatar.Root>

        <div className="flex-1 min-w-0">
          <div className="flex flex-row justify-between items-start">
            <div>
              <span className="inline-flex items-center gap-1 text-sm font-semibold">
                {comment.userActionUrl ? (
                  <Link
                    className="hover:underline"
                    href={`/committee/${comment.userActionUrl.split("=").at(-1)}`}
                  >
                    {comment.userName}
                  </Link>
                ) : (
                  comment.userName
                )}
                {comment.isVerified && (
                  <Image alt="verified" height={14} src={verified} width={14} />
                )}
                <span className="text-xs font-normal text-neutral-400 ml-1">
                  {comment.createdAtLabel}
                </span>
              </span>
              <p className="text-xs text-neutral-500">{comment.userTitle}</p>
            </div>

            <Menu.Root>
              <Menu.Trigger render={<Button size="icon-sm" variant="ghost" />}>
                <ElipsisIcon className="size-5" />
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner align="end" sideOffset={4}>
                  <Menu.Popup className="min-w-40 rounded-xl border border-neutral-200 bg-white shadow-lg py-1 outline-none z-50">
                    {comment.isOwner ? (
                      <>
                        <Menu.Item
                          className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 cursor-pointer hover:bg-neutral-50 data-highlighted:bg-neutral-50 outline-none"
                          onClick={() => setIsEditing(true)}
                        >
                          <Pencil className="size-4 stroke-neutral-400" />
                          Düzenle
                        </Menu.Item>
                        <Menu.Item
                          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 cursor-pointer hover:bg-red-50 data-highlighted:bg-red-50 outline-none"
                          onClick={() => setDeleteOpen(true)}
                        >
                          <Trash className="size-4" />
                          Sil
                        </Menu.Item>
                      </>
                    ) : (
                      <Menu.Item
                        className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 cursor-pointer hover:bg-neutral-50 data-highlighted:bg-neutral-50 outline-none"
                        onClick={() => setReportOpen(true)}
                      >
                        <Flag className="size-4 stroke-neutral-400" />
                        Bildir
                      </Menu.Item>
                    )}
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          </div>

          <Dialog.Root onOpenChange={setReportOpen} open={reportOpen}>
            <Dialog.Portal>
              <Dialog.Backdrop className="fixed inset-0 bg-black/40 transition-all duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0" />
              <Dialog.Popup className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl outline-none transition-all duration-150 data-starting-style:scale-90 data-starting-style:opacity-0 data-ending-style:scale-90 data-ending-style:opacity-0">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Yorumu Bildir
                    </Dialog.Title>
                    <Dialog.Close
                      className="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
                      render={<button type="button" />}
                    >
                      ✕
                    </Dialog.Close>
                  </div>
                  <Dialog.Description className="text-sm text-gray-500">
                    Bu yorumu şikayet etmek istediğinizden emin misiniz?
                  </Dialog.Description>
                  <div className="flex gap-2 justify-end">
                    <Dialog.Close
                      render={
                        <Button size="sm" type="button" variant="outline" />
                      }
                    >
                      Vazgeç
                    </Dialog.Close>
                    <Button onClick={handleReport} size="sm" type="button">
                      Bildir
                    </Button>
                  </div>
                </div>
              </Dialog.Popup>
            </Dialog.Portal>
          </Dialog.Root>

          <Dialog.Root onOpenChange={setDeleteOpen} open={deleteOpen}>
            <Dialog.Portal>
              <Dialog.Backdrop className="fixed inset-0 bg-black/40 transition-all duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0" />
              <Dialog.Popup className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl outline-none transition-all duration-150 data-starting-style:scale-90 data-starting-style:opacity-0 data-ending-style:scale-90 data-ending-style:opacity-0">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Yorumu Sil
                    </Dialog.Title>
                    <Dialog.Close
                      className="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
                      render={<button type="button" />}
                    >
                      ✕
                    </Dialog.Close>
                  </div>
                  <Dialog.Description className="text-sm text-gray-500">
                    Bu yorumu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                  </Dialog.Description>
                  <div className="flex gap-2 justify-end">
                    <Dialog.Close
                      render={
                        <Button size="sm" type="button" variant="outline" />
                      }
                    >
                      Vazgeç
                    </Dialog.Close>
                    <Button
                      disabled={isDeletePending}
                      onClick={() => deleteMutate()}
                      size="sm"
                      type="button"
                      variant="destructive"
                    >
                      Sil
                    </Button>
                  </div>
                </div>
              </Dialog.Popup>
            </Dialog.Portal>
          </Dialog.Root>

          {isEditing ? (
            <div className="mt-2">
              <TopicReplyEditor
                autoFocus={true}
                initialContent={comment.text}
                isPending={isEditPending}
                onCancel={() => setIsEditing(false)}
                onSubmit={(html, images) => editMutate({ html, images })}
                placeholder="Yorumunu düzenle..."
              />
            </div>
          ) : (
            <div className="text-sm mt-2 text-neutral-700 leading-relaxed">
              <div dangerouslySetInnerHTML={{ __html: displayText }} />
              {isLong && (
                <button
                  className="inline-flex items-center gap-0.5 text-brand-blue-500 text-sm mt-1 hover:underline"
                  onClick={() => setExpanded((e) => !e)}
                  type="button"
                >
                  {expanded ? "Kapat" : "Devamı"}
                  <ChevronDown
                    className={`size-4 stroke-brand-blue-500 transition-transform ${expanded ? "rotate-180" : ""}`}
                  />
                </button>
              )}
            </div>
          )}

          <div className="flex flex-row items-center gap-2 mt-3">
            <CommentLikeButton
              commentId={comment.id}
              contentId={contentId}
              initialCount={comment.likeCount}
              initialLiked={comment.isLiked}
            />
            {!isReply && (
              <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                <MessageIcon className="size-4 stroke-neutral-400" />
                {comment.replyCount}
              </span>
            )}
            {!isReply && (
              <Button
                className="text-brand-blue-500 text-xs h-auto py-1"
                onClick={() => setShowReplyForm((v) => !v)}
                size="xs"
                variant="ghost"
              >
                Yanıtla
              </Button>
            )}
          </div>

          {showReplyForm && isLoggedIn && (
            <CommentReplyForm
              commentId={comment.id}
              contentId={contentId}
              currentUser={currentUser}
              onClose={() => setShowReplyForm(false)}
              onSuccess={() => setRepliesExpanded(true)}
            />
          )}

          {!isReply && (comment.replyCount > 0 || repliesExpanded) && (
            <CommentReplies
              commentId={comment.id}
              contentId={contentId}
              currentUser={currentUser}
              expanded={repliesExpanded}
              isLoggedIn={isLoggedIn}
              onExpand={() => setRepliesExpanded(true)}
              replyCount={comment.replyCount}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentCard;
